import * as Yup from "yup";
import _ from "lodash";
import datehelper from "../common/helper/datehelper";

// Helper function to check for duplicate filenames
function hasDuplicates(array) {
  return array.length !== _.uniq(array).length;
}

const regex = {
  alphabetOnly: /^[a-zA-Z ]*$/,
  alphabetAndNumber: /^[a-zA-Z0-9 ]*$/,
  alphaNumeric: /^[a-zA-Z0-9]*$/,
  alphaNumericHyphenAndForwardSlash: /^[a-zA-Z0-9/-]*$/,
  numberOnly: /^[0-9]*$/,
};

export const validationSchema = {
  //Location
  locationSelect: Yup.object().shape({
    country: Yup.string().required("Country is required."),
  }),

  //Loan Info
  loanAmount: Yup.number()
    .required("Fund amount is required.")
    .typeError("Fund amount is required.")
    .moreThan("10", "Fund amount should be more than 10 USD."),
  loanTenure: Yup.number()
    .required("Teerm is required.")
    .typeError("Term is required.")
    .moreThan("1", "Loan tenure should be a between 2 and 60.")
    .lessThan("61", "Loan tenure should be a between 2 and 60."),
  loanPurpose: Yup.string()
    .required("Loan purpose is required.")
    .matches(regex.alphabetAndNumber, "Please enter valid loan purpose."),

  // Business
  businessCountry: Yup.object().shape({
    country: Yup.string().required("Country is required."),
  }),

  businessAddrCountry: Yup.object().shape({
    country: Yup.string().required("Country is required."),
  }),

  businessRegNum: Yup.string()
    .required("ABN number is required.")
    .matches(
      regex.alphaNumericHyphenAndForwardSlash,
      "Please enter valid ABN number."
    ),

  businessName: Yup.string()
    .required("Entity name is required.")
    .matches(regex.alphabetAndNumber, "Please enter valid entity name.")
    .min(2, "Minimum 2 characters is required."),

  entityType: Yup.string()
    .required("Entity type is required.")
    .matches(regex.alphabetAndNumber, "Please enter valid entity type."),

  businessDate: Yup.date()
    .required("Registration date is required.")
    .typeError("Please enter valid registration date.")
    .max(datehelper.getYesterdayDate(), "Registration date should be past date."),

  industry: Yup.string().required("Industry or Sector is required."),

  businessAddr: Yup.string()
    .required("Street address is required.")
    .matches(regex.alphabetAndNumber, "Please enter valid street address.")
    .max(500, "Street address must be less than 500 characters."),

  businessZipCode: Yup.string()
    .required("Areacode is required.")
    .matches(regex.alphaNumeric, "Please enter valid areacode."),

  businessEmail: Yup.string()
    .email("Please enter valid email address.")
    .required("Email address is required."),

    businessPhone: Yup.string()
    .required("Mobile Number is mandatory")
    .matches(/^\d{10}$/, "Please enter a valid 10-digit mobile number"),

  // Applicant
  applicantName: Yup.string()
    .required("Applicant name is required.")
    .matches(regex.alphabetOnly, "Please enter valid applicant name."),

  applicantNationality: Yup.object().shape({
    country: Yup.string().required("Applicant nationality is required."),
  }),

  residentialStatus: Yup.string()
    .required("Residential status is required.")
    .matches(regex.alphabetOnly, "Please enter valid residential status."),

  residentialAddr: Yup.string()
    .required("Street address is required.")
    .matches(regex.alphabetAndNumber, "Please enter valid street address.")
    .max(500, "Street address must be less than 500 characters."),

  applicantZip: Yup.string()
    .required("Area code is required.")
    .matches(regex.alphaNumeric, "Please enter valid area code."),

  applicantEmail: Yup.string()
    .email("Please enter valid applicant email address.")
    .required("Applicant email address is required."),

  applicantPhone: Yup.string().required("Phone number is required."),

  // Documents
  bankStatements: Yup.mixed()
    .test("required.", "Please upload bank statements", (file) => {
      if (file && file.length) {
        var inValidFiles = [];
        for (var f = 0; f < file.length; f++) {
          if (
            _.includes(
              ["pdf"],
              _.toLower(_.last(_.split(file[f]?.name, ".")))
            ) === false
          ) {
            inValidFiles.push(file[f]);
          }
        }
        return inValidFiles.length === 0;
      }
      return false;
    })
    .test("required.", "File with this name already exists.", (file) => {
      if (file && file.length) {
        const isValid = hasDuplicates(_.map(file, "name"));
        return !isValid;
      } else {
        return true;
      }
    }),

  financeStatements: Yup.mixed()
    .test("required.", "Please upload financial statements", (file) => {
      if (file && file.length) {
        var inValidFiles = [];
        for (var f = 0; f < file.length; f++) {
          if (
            _.includes(
              ["pdf", "png", "jpeg", "jpg"],
              _.toLower(_.last(_.split(file[f]?.name, ".")))
            ) === false
          ) {
            inValidFiles.push(file[f]);
          }
        }
        return inValidFiles.length === 0;
      }
      return false;
    })
    .test("required.", "File with this name already exists.", (file) => {
      if (file && file.length) {
        const isValid = hasDuplicates(_.map(file, "name"));
        return !isValid;
      } else {
        return true;
      }
    }),

  gstStatements: Yup.mixed()
    .test("required.", "Please upload GST statements", (file) => {
      if (file && file.length) {
        var inValidFiles = [];
        for (var f = 0; f < file.length; f++) {
          if (
            _.includes(
              ["pdf"],
              _.toLower(_.last(_.split(file[f]?.name, ".")))
            ) === false
          ) {
            inValidFiles.push(file[f]);
          }
        }
        return inValidFiles.length === 0;
      }
      return false;
    })
    .test("required.", "File with this name already exists.", (file) => {
      if (file && file.length) {
        const isValid = hasDuplicates(_.map(file, "name"));
        return !isValid;
      } else {
        return true;
      }
    }),

  revenueFinancing: Yup.mixed()
    .test("required.", "Please upload Revenue Detail Document.", (file) => {
      if (file && file.length) {
        var inValidFiles = [];
        for (var f = 0; f < file.length; f++) {
          if (
            _.includes(
              ["xlsx"],
              _.toLower(_.last(_.split(file[f]?.name, ".")))
            ) === false
          ) {
            inValidFiles.push(file[f]);
          }
        }
        if (inValidFiles.length > 0) {
          return false;
        } else {
          return true;
        }
      }
    })
    .test("required.", "File with this name already exists.", (file) => {
      if (file && file.length) {
        const isValid = hasDuplicates(_.map(file, "name"));
        return !isValid;
      } else {
        return true;
      }
    }),

  businessRegCert: Yup.mixed()
    .test("required.", "Please upload business registration certificate", (file) => {
      if (file && file.length) {
        var inValidFiles = [];
        for (var f = 0; f < file.length; f++) {
          if (
            _.includes(
              ["pdf", "png", "jpeg", "jpg"],
              _.toLower(_.last(_.split(file[f]?.name, ".")))
            ) === false
          ) {
            inValidFiles.push(file[f]);
          }
        }
        return inValidFiles.length === 0;
      }
      return false;
    })
    .test("required.", "File with this name already exists.", (file) => {
      if (file && file.length) {
        const isValid = hasDuplicates(_.map(file, "name"));
        return !isValid;
      } else {
        return true;
      }
    }),

  // Director1
  directorName1: Yup.string()
    .required("Director name is required.")
    .matches(regex.alphabetOnly, "Please enter valid director name."),

  driverLicenseNumber: Yup.string().required("Driver license number is required."),

  personalIdentificationNumber1: Yup.string()
    .required("Personal identification number is required.")
    .typeError("Personal identification number is required.")
    .matches(regex.alphaNumeric, "Please enter valid personal identification number."),

  shareholding1: Yup.number()
    .required("Share holding is required.")
    .typeError("Share holding is required.")
    .moreThan("0", "Share holding should be a between 1 and 100.")
    .lessThan("101", "Share holding should be a between 1 and 100."),

  directorAddress1: Yup.string()
    .required("Street address is required.")
    .matches(regex.alphabetAndNumber, "Please enter valid street address.")
    .max(500, "Street address must be less than 500 characters."),

  directorZipCode1: Yup.string()
    .required("Zipcode is required.")
    .matches(regex.alphaNumeric, "Please enter valid zipcode."),

  directorCountry1: Yup.object().shape({
    country: Yup.string().required("Country is required."),
  }),

  directorPhone1: Yup.string()
    .required("Mobile number is mandatory")
    .matches(/^\d{10}$/, "Please enter a valid 10-digit mobile number"),

  directorEmail1: Yup.string()
    .email("Please enter valid email address.")
    .required("Email address is required."),

  directorsuburb: Yup.string()
    .required("Suburb is required.")
    .matches(regex.alphabetOnly, "Please enter valid suburb."),

  directorState: Yup.string()
    .required("State / Territory is required.")
    .matches(regex.alphabetOnly, "Please enter valid state / territory."),

  videoCallDate1: Yup.date()
    .required("Video call date is required.")
    .typeError("Please enter valid registration video call date.")
    .min(datehelper.getTodayDate(), "Video call date should be future date."),

  videoCallTime1: Yup.date()
    .required("Video call time is required.")
    .typeError("Please enter valid registration video call time."),

  passport2: Yup.mixed()
    .test("required.", "Please upload passport images/pdf.", (file) => {
      if (file && file.length) return true;
      else return false;
    })
    .test("required.", "Please upload pdf, png, jpeg or jpg files only", (file) => {
      if (file && file.length) {
        var inValidFiles = [];
        for (var f = 0; f < file.length; f++) {
          if (
            _.includes(
              ["pdf", "png", "jpeg", "jpg"],
              _.toLower(_.last(_.split(file[f]?.name, ".")))
            ) === false
          ) {
            inValidFiles.push(file[f]);
          }
        }
        if (inValidFiles.length > 0) {
          return false;
        } else {
          return true;
        }
      }
    })
    .test("required", "You can upload maximum 5 passports.", (file) => {
      if (file && file.length > 5) {
        return false;
      } else {
        return true;
      }
    })
    .test("required.", "File with this name already exists.", (file) => {
      if (file && file.length) {
        const isValid = hasDuplicates(_.map(file, "name"));
        return !isValid;
      } else {
        return true;
      }
    }),

  utilityBills2: Yup.mixed()
    .test("required.", "Please upload utility bills", (file) => {
      if (file && file.length) return true;
      else return false;
    })
    .test("required.", "Please upload pdf, png, jpeg or jpg files only", (file) => {
      if (file && file.length) {
        var inValidFiles = [];
        for (var f = 0; f < file.length; f++) {
          if (
            _.includes(
              ["pdf", "png", "jpeg", "jpg"],
              _.toLower(_.last(_.split(file[f]?.name, ".")))
            ) === false
          ) {
            inValidFiles.push(file[f]);
          }
        }
        if (inValidFiles.length > 0) {
          return false;
        } else {
          return true;
        }
      }
    })
    .test("required", "You can upload maximum 5 utility bills.", (file) => {
      if (file && file.length > 5) {
        return false;
      } else {
        return true;
      }
    })
    .test("required.", "File with this name already exists.", (file) => {
      if (file && file.length) {
        const isValid = hasDuplicates(_.map(file, "name"));
        return !isValid;
      } else {
        return true;
      }
    }),
};

export const director2FormSchema = {
  directorName2: Yup.string()
    .required("Director name is required.")
    .matches(regex.alphabetOnly, "Please enter valid director name."),

  personalIdentificationNumber2: Yup.string()
    .required("Personal identification number is required.")
    .typeError("Personal identification number is required.")
    .matches(regex.alphaNumeric, "Please enter valid personal identification number."),

  shareholding2: Yup.number()
    .required("Share holding is required.")
    .typeError("Share holding is required.")
    .moreThan("0", "Share holding should be a between 1 and 100.")
    .lessThan("101", "Share holding should be a between 1 and 100."),

  directorAddress2: Yup.string()
    .required("Residential address is required.")
    .matches(regex.alphabetAndNumber, "Please enter valid residential address.")
    .max(500, "Residential address must be less than 500 characters."),

  directorZipCode2: Yup.string()
    .required("Zipcode is required.")
    .matches(regex.alphaNumeric, "Please enter valid zipcode."),

  directorCountry2: Yup.object().shape({
    country: Yup.string().required("Country is required."),
  }),

  directorPhone2: Yup.string().required("Phone number is required."),

  directorEmail2: Yup.string()
    .email("Please enter valid email address.")
    .required("Email address is required."),

  videoCallDate2: Yup.date()
    .required("Video call date is required.")
    .typeError("Please enter valid registration video call date.")
    .min(datehelper.getTodayDate(), "Video call date should be future date."),

  videoCallTime2: Yup.date()
    .required("Video call time is required.")
    .typeError("Please enter valid registration video call time."),
};
